import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import { Wrapper } from "../../components";
import Loading from "../../components/Loading/Loading";
import {
  createPromotion,
  deletePromotion,
  getCategories,
  getPromotions,
  getPromotion,
  searchProductsForPromotion,
  togglePromotion,
  updatePromotion,
} from "../../services/PromotionService";
import "./promotions.css";

const emptyForm = {
  name: "",
  description: "",
  discount_type: "percentage",
  discount_value: "",
  start_date: "",
  end_date: "",
  scope: "all",
  category_id: "",
  active: true,
  product_ids: [],
};

const formatDiscount = (promotion) => {
  if (promotion.discount_type === "percentage") {
    return `${promotion.discount_value}% OFF`;
  }
  return `D${promotion.discount_value} OFF`;
};

const formatScope = (promotion) => {
  if (promotion.scope === "all") return "All products";
  if (promotion.scope === "category") {
    return promotion.category_name
      ? `Category: ${promotion.category_name}`
      : "Selected category";
  }
  return `${promotion.product_count || 0} selected product(s)`;
};

const PromotionFormDialog = ({
  open,
  onClose,
  onSaved,
  initialData,
  categories,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        description: initialData.description || "",
        discount_type: initialData.discount_type || "percentage",
        discount_value: initialData.discount_value || "",
        start_date: initialData.start_date || "",
        end_date: initialData.end_date || "",
        scope: initialData.scope || "all",
        category_id: initialData.category_id || "",
        active: Boolean(initialData.active),
        product_ids: initialData.product_ids || [],
      });
      const ids = initialData.product_ids || [];
      setSelectedProducts(
        ids.map((id) => ({ id, product_id: id, name: `Product #${id}` }))
      );
    } else {
      setForm(emptyForm);
      setSelectedProducts([]);
    }
    setProductSearch("");
    setProductResults([]);
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearchProducts = async () => {
    const results = await searchProductsForPromotion(productSearch);
    setProductResults(results.slice(0, 20));
  };

  const toggleProductSelection = (product) => {
    const productId = product.product_id || product.id;
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => (p.product_id || p.id) === productId);
      if (exists) {
        return prev.filter((p) => (p.product_id || p.id) !== productId);
      }
      return [...prev, product];
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload = {
      ...form,
      discount_value: Number(form.discount_value),
      category_id: form.scope === "category" ? Number(form.category_id) : null,
      product_ids: form.scope === "products"
        ? selectedProducts.map((p) => p.product_id || p.id)
        : [],
    };

    const result = initialData?.id
      ? await updatePromotion(initialData.id, payload)
      : await createPromotion(payload);

    setSaving(false);

    if (result?.success) {
      onSaved();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData?.id ? "Edit Promotion" : "Create Promotion"}
      </DialogTitle>
      <DialogContent dividers>
        <div className="promo-form-section">
          <div className="promo-form-section-title">Promotion details</div>
          <TextField
            label="Promotion name"
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            margin="dense"
            placeholder="e.g. Ramadan Sale"
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={handleChange("description")}
            fullWidth
            margin="dense"
            multiline
            rows={2}
            placeholder="Optional short description"
          />
        </div>

        <div className="promo-form-section">
          <div className="promo-form-section-title">Discount</div>
          <FormControl fullWidth margin="dense">
            <InputLabel>Discount type</InputLabel>
            <Select
              value={form.discount_type}
              onChange={handleChange("discount_type")}
            >
              <MenuItem value="percentage">Percentage (%)</MenuItem>
              <MenuItem value="fixed">Fixed amount (D)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={form.discount_type === "percentage" ? "Discount (%)" : "Discount amount (D)"}
            type="number"
            value={form.discount_value}
            onChange={handleChange("discount_value")}
            fullWidth
            margin="dense"
          />
        </div>

        <div className="promo-form-section">
          <div className="promo-form-section-title">Promotion period</div>
          <TextField
            label="Start date"
            type="date"
            value={form.start_date}
            onChange={handleChange("start_date")}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End date"
            type="date"
            value={form.end_date}
            onChange={handleChange("end_date")}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div className="promo-form-section">
          <div className="promo-form-section-title">Applies to</div>
          <FormControl fullWidth margin="dense">
            <InputLabel>Scope</InputLabel>
            <Select value={form.scope} onChange={handleChange("scope")}>
              <MenuItem value="all">All products</MenuItem>
              <MenuItem value="category">Specific category</MenuItem>
              <MenuItem value="products">Selected products</MenuItem>
            </Select>
          </FormControl>

          {form.scope === "category" && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Category</InputLabel>
              <Select
                value={form.category_id}
                onChange={handleChange("category_id")}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.category_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {form.scope === "products" && (
            <>
              <TextField
                label="Search products"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                fullWidth
                margin="dense"
                onKeyDown={(e) => e.key === "Enter" && handleSearchProducts()}
              />
              <Button
                color="primary"
                size="small"
                onClick={handleSearchProducts}
                style={{ marginBottom: 8 }}
              >
                Search products
              </Button>
              <div className="promo-product-list">
                {productResults.map((product) => {
                  const productId = product.product_id || product.id;
                  const checked = selectedProducts.some(
                    (p) => (p.product_id || p.id) === productId
                  );
                  return (
                    <div key={productId} className="promo-product-item">
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleProductSelection(product)}
                        color="primary"
                        size="small"
                      />
                      <span>{product.name}</span>
                    </div>
                  );
                })}
                {!productResults.length && (
                  <Typography variant="caption" color="textSecondary">
                    Search and select products for this promotion.
                  </Typography>
                )}
              </div>
              {selectedProducts.length > 0 && (
                <Typography variant="caption" color="primary">
                  {selectedProducts.length} product(s) selected
                </Typography>
              )}
            </>
          )}
        </div>

        <FormControlLabel
          control={
            <Checkbox
              checked={form.active}
              onChange={handleChange("active")}
              color="primary"
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "Saving..." : initialData?.id ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManagePromotions = () => {
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    scheduled: 0,
    expired: 0,
  });
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const loadPromotions = async () => {
    setLoading(true);
    try {
      const response = await getPromotions();
      if (response.success) {
        setPromotions(response.data.promotions || []);
        setStats(response.data.stats || stats);
      }
    } catch (error) {
      setPromotions([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPromotions();
    getCategories().then(setCategories);
  }, []);

  const handleEdit = async (promotion) => {
    try {
      const response = await getPromotion(promotion.id);
      if (response.success) {
        setEditData(response.data);
        setDialogOpen(true);
      }
    } catch (error) {
      setEditData(promotion);
      setDialogOpen(true);
    }
  };

  const handleDelete = async (id) => {
    const result = await deletePromotion(id);
    if (result?.success) {
      loadPromotions();
    }
  };

  const handleToggle = async (id) => {
    const result = await togglePromotion(id);
    if (result?.success) {
      loadPromotions();
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Wrapper>
      <div className="promotions-page">
        <div className="promotions-header">
          <div>
            <Typography variant="h5">Manage Promotions</Typography>
            <Typography variant="body2" color="textSecondary">
              Create and manage discount campaigns for promotion periods.
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditData(null);
              setDialogOpen(true);
            }}
          >
            New Promotion
          </Button>
        </div>

        <div className="promotions-stats">
          <div className="promo-stat-card total">
            <div className="promo-stat-label">Total promotions</div>
            <div className="promo-stat-value">{stats.total}</div>
          </div>
          <div className="promo-stat-card active">
            <div className="promo-stat-label">Active now</div>
            <div className="promo-stat-value">{stats.active}</div>
          </div>
          <div className="promo-stat-card scheduled">
            <div className="promo-stat-label">Scheduled</div>
            <div className="promo-stat-value">{stats.scheduled}</div>
          </div>
          <div className="promo-stat-card expired">
            <div className="promo-stat-label">Expired</div>
            <div className="promo-stat-value">{stats.expired}</div>
          </div>
        </div>

        {promotions.length === 0 ? (
          <div className="promo-empty">
            <LocalOfferIcon style={{ fontSize: 48, color: "#9fa8da" }} />
            <Typography variant="h6" style={{ marginTop: 12 }}>
              No promotions yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Create your first promotion to offer discounts during a campaign period.
            </Typography>
          </div>
        ) : (
          <div className="promotions-grid">
            {promotions.map((promotion) => (
              <div className="promo-card" key={promotion.id}>
                <div className="promo-card-banner">
                  <h6>{promotion.name}</h6>
                  <p>{promotion.description || "No description provided"}</p>
                </div>
                <div className="promo-card-body">
                  <div className="promo-discount-badge">
                    {formatDiscount(promotion)}
                  </div>
                  <div className="promo-meta-row">
                    <span>Period</span>
                    <strong>
                      {promotion.start_date} → {promotion.end_date}
                    </strong>
                  </div>
                  <div className="promo-meta-row">
                    <span>Applies to</span>
                    <strong>{formatScope(promotion)}</strong>
                  </div>
                  <div className="promo-meta-row">
                    <span>Status</span>
                    <span className={`promo-status-chip ${promotion.status}`}>
                      {promotion.status}
                    </span>
                  </div>
                </div>
                <div className="promo-card-actions">
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(promotion)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<PowerSettingsNewIcon />}
                    onClick={() => handleToggle(promotion.id)}
                  >
                    {promotion.active ? "Deactivate" : "Activate"}
                  </Button>
                  <Button
                    size="small"
                    color="secondary"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(promotion.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PromotionFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={loadPromotions}
        initialData={editData}
        categories={categories}
      />
    </Wrapper>
  );
};

export default ManagePromotions;
